import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import changelogData from '../../assets/changelog.json';

interface ChangelogItem {
  version: string;
  date: string;
  title: string;
  changes: string[];
  userChanges?: string[];
}

@Component({
  selector: 'app-changelog-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  template: `
    <h2 mat-dialog-title class="changelog-dialog-title">
      <mat-icon>history_edu</mat-icon>
      <span>App Version History</span>
    </h2>

    <div mat-dialog-content class="changelog-dialog-content">
      @for (release of changelog(); track release.version) {
        <div class="changelog-card">
          <div class="changelog-card-header">
            <div class="version-badge">v{{ release.version }}</div>
            <div class="release-date">{{ release.date | date:'mediumDate' }}</div>
          </div>
          <h3 class="changelog-card-title">{{ release.title }}</h3>
          <ul class="changelog-logs-list">
            @for (change of release.changes; track change) {
              @let item = parseChange(change);
              <li class="changelog-log-item">
                @if (item.type) {
                  <span class="chip chip-{{ item.type.toLowerCase() }}">{{ item.type }}</span>
                }
                <span class="log-text">{{ item.text }}</span>
              </li>
            }
          </ul>
        </div>
      }
    </div>

    <div mat-dialog-actions class="changelog-dialog-actions">
      <span class="spacer"></span>
      <button mat-flat-button color="primary" [mat-dialog-close]="true">Got it</button>
    </div>
  `,
  styles: [`
    .changelog-dialog-title {
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 0;
      padding: 16px 24px;
      font-weight: 500;
      color: var(--mat-sys-on-surface, #323123);
      border-bottom: 1px solid var(--mat-sys-outline-variant, rgba(0,0,0,0.08));
    }
    .changelog-dialog-title mat-icon {
      color: var(--mat-sys-primary, #666000);
    }
    .changelog-dialog-content {
      padding: 20px 24px !important;
      max-height: 420px;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .changelog-card {
      background: var(--app-item-background, #faf9f5);
      border: 1px solid var(--mat-sys-outline-variant, rgba(0,0,0,0.05));
      border-radius: 12px;
      padding: 16px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.02);
    }
    .changelog-card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
    }
    .version-badge {
      background: var(--mat-sys-primary-container, #fffae1);
      color: var(--mat-sys-on-primary-container, #666000);
      font-weight: bold;
      font-size: 0.9em;
      padding: 4px 10px;
      border-radius: 20px;
      border: 1px solid rgba(156, 148, 0, 0.15);
    }
    .release-date {
      font-size: 0.85em;
      color: var(--mat-sys-on-surface-variant, #7a7766);
    }
    .changelog-card-title {
      margin: 0 0 12px 0;
      font-size: 1.1em;
      font-weight: 500;
      color: var(--mat-sys-on-surface, #323123);
    }
    .changelog-logs-list {
      margin: 0;
      padding: 0;
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .changelog-log-item {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      font-size: 0.95em;
      line-height: 1.4;
      color: var(--mat-sys-on-surface-variant, #494738);
    }
    .chip {
      font-size: 0.72em;
      font-weight: 700;
      text-transform: uppercase;
      padding: 2px 8px;
      border-radius: 4px;
      flex-shrink: 0;
      margin-top: 1px;
      letter-spacing: 0.03em;
      background: rgba(0, 0, 0, 0.05);
      color: var(--mat-sys-on-surface-variant, #494738);
      border: 1px solid rgba(0, 0, 0, 0.12);
    }
    .chip-feature {
      background: rgba(76, 175, 80, 0.12);
      color: #2e7d32;
      border: 1px solid rgba(76, 175, 80, 0.25);
    }
    .chip-refinement {
      background: rgba(33, 150, 243, 0.12);
      color: #1565c0;
      border: 1px solid rgba(33, 150, 243, 0.25);
    }
    .chip-fix {
      background: rgba(244, 67, 54, 0.12);
      color: #c62828;
      border: 1px solid rgba(244, 67, 54, 0.25);
    }
    .chip-refactor {
      background: rgba(156, 39, 176, 0.12);
      color: #7b1fa2;
      border: 1px solid rgba(156, 39, 176, 0.25);
    }
    .chip-performance {
      background: rgba(255, 152, 0, 0.14);
      color: #e65100;
      border: 1px solid rgba(255, 152, 0, 0.28);
    }
    .chip-security {
      background: rgba(233, 30, 99, 0.12);
      color: #ad1457;
      border: 1px solid rgba(233, 30, 99, 0.25);
    }
    .chip-docs {
      background: rgba(96, 125, 139, 0.12);
      color: #455a64;
      border: 1px solid rgba(96, 125, 139, 0.25);
    }
    .chip-admin {
      background: rgba(103, 58, 183, 0.12);
      color: #512da8;
      border: 1px solid rgba(103, 58, 183, 0.25);
    }
    .chip-chore {
      background: rgba(117, 117, 117, 0.12);
      color: #424242;
      border: 1px solid rgba(117, 117, 117, 0.25);
    }
    .chip-style {
      background: rgba(0, 150, 136, 0.12);
      color: #00695c;
      border: 1px solid rgba(0, 150, 136, 0.25);
    }
    .chip-roadmap {
      background: rgba(0, 188, 212, 0.12);
      color: #00838f;
      border: 1px solid rgba(0, 188, 212, 0.25);
    }
    .log-text {
      flex: 1;
    }
    .changelog-dialog-actions {
      padding: 12px 24px !important;
      border-top: 1px solid var(--mat-sys-outline-variant, rgba(0,0,0,0.08));
      display: flex;
      justify-content: flex-end;
    }
    .spacer {
      flex: 1 1 auto;
    }

    /* Webkit Scrollbar */
    .changelog-dialog-content::-webkit-scrollbar {
      width: 6px;
    }
    .changelog-dialog-content::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.02);
    }
    .changelog-dialog-content::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.15);
      border-radius: 3px;
    }

    /* Dark Mode Overrides */
    :host-context(.dark-theme) .changelog-card {
      background: rgba(255, 255, 255, 0.04);
      border-color: rgba(255, 255, 255, 0.08);
      box-shadow: none;
    }
    :host-context(.dark-theme) .changelog-dialog-title {
      color: #ffffff;
      border-color: rgba(255, 255, 255, 0.12);
    }
    :host-context(.dark-theme) .changelog-dialog-title mat-icon {
      color: #fff100;
    }
    :host-context(.dark-theme) .changelog-card-title {
      color: #ffffff;
    }
    :host-context(.dark-theme) .release-date {
      color: rgba(255, 255, 255, 0.6);
    }
    :host-context(.dark-theme) .changelog-log-item {
      color: rgba(255, 255, 255, 0.85);
    }
    :host-context(.dark-theme) .version-badge {
      background: rgba(255, 241, 0, 0.08);
      color: #fff100;
      border-color: rgba(255, 241, 0, 0.2);
    }
    :host-context(.dark-theme) .changelog-dialog-content::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.01);
    }
    :host-context(.dark-theme) .changelog-dialog-content::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.12);
    }
    :host-context(.dark-theme) .chip {
      background: rgba(255, 255, 255, 0.08);
      color: rgba(255, 255, 255, 0.7);
      border-color: rgba(255, 255, 255, 0.12);
    }
    :host-context(.dark-theme) .chip-feature {
      background: rgba(129, 199, 132, 0.15);
      color: #81c784;
      border-color: rgba(129, 199, 132, 0.25);
    }
    :host-context(.dark-theme) .chip-refinement {
      background: rgba(100, 181, 246, 0.15);
      color: #64b5f6;
      border-color: rgba(100, 181, 246, 0.25);
    }
    :host-context(.dark-theme) .chip-fix {
      background: rgba(229, 115, 115, 0.15);
      color: #e57373;
      border-color: rgba(229, 115, 115, 0.25);
    }
    :host-context(.dark-theme) .chip-refactor {
      background: rgba(186, 104, 200, 0.15);
      color: #ba68c8;
      border-color: rgba(186, 104, 200, 0.25);
    }
    :host-context(.dark-theme) .chip-performance {
      background: rgba(255, 183, 77, 0.15);
      color: #ffb74d;
      border-color: rgba(255, 183, 77, 0.25);
    }
    :host-context(.dark-theme) .chip-security {
      background: rgba(240, 98, 146, 0.15);
      color: #f06292;
      border-color: rgba(240, 98, 146, 0.25);
    }
    :host-context(.dark-theme) .chip-docs {
      background: rgba(96, 125, 139, 0.12);
      color: #b0bec5;
      border-color: rgba(96, 125, 139, 0.25);
    }
    :host-context(.dark-theme) .chip-admin {
      background: rgba(179, 157, 219, 0.15);
      color: #b39ddb;
      border-color: rgba(179, 157, 219, 0.25);
    }
    :host-context(.dark-theme) .chip-chore {
      background: rgba(189, 189, 189, 0.15);
      color: #bdbdbd;
      border-color: rgba(189, 189, 189, 0.25);
    }
    :host-context(.dark-theme) .chip-style {
      background: rgba(77, 182, 172, 0.15);
      color: #4db6ac;
      border-color: rgba(77, 182, 172, 0.25);
    }
    :host-context(.dark-theme) .chip-roadmap {
      background: rgba(77, 208, 225, 0.15);
      color: #4dd0e1;
      border-color: rgba(77, 208, 225, 0.25);
    }
  `]
})
export class ChangelogDialogComponent {
  private authService = inject(AuthService);

  isAdmin = toSignal(
    this.authService.userData$.pipe(
      map(user => user?.role === 'admin')
    ),
    { initialValue: false }
  );

  changelog = computed(() => {
    const admin = this.isAdmin();
    return (changelogData as ChangelogItem[]).map(release => {
      const list = (!admin && release.userChanges) ? release.userChanges : release.changes;
      return {
        ...release,
        changes: list
      };
    });
  });

  parseChange(change: string): { type: string; text: string } {
    const match = change.match(/^\[([A-Za-z0-9_\s-]+)\]\s*(.*)$/);
    if (match) {
      return { type: match[1].trim(), text: match[2] };
    }
    return { type: '', text: change };
  }
}
